
import React, { useState } from 'react';
import { formatDistanceToNow } from "date-fns";
import { 
  Archive, 
  Flag, 
  Link as LinkIcon, 
  RefreshCw, 
  Tag, 
  LayoutGrid, 
  ChevronDown,
  ChevronUp,
  Info,
  Clock,
  User,
  Building,
  Target
} from "lucide-react";
import { EmailSummaryForHook } from "./types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface ImprovedEmailCardProps {
  email: EmailSummaryForHook;
  onArchive: (emailId: string) => void;
  onFlag: (emailId: string, currentFlagStatus: boolean) => void;
  isArchiving: boolean;
  isFlagging: boolean;
}

const getPriorityDisplay = (score?: number | null) => {
  if (score === null || score === undefined) return null;
  
  if (score >= 7) {
    return { 
      label: "High Priority", 
      emoji: "🔥", 
      color: "bg-amber-100 text-amber-800 border-amber-200",
      description: "Needs immediate attention"
    };
  }
  if (score >= 4) {
    return { 
      label: "Medium Priority", 
      emoji: "⚡", 
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      description: "Should be addressed soon"
    };
  }
  return { 
    label: "Low Priority", 
    emoji: "✅", 
    color: "bg-green-100 text-green-800 border-green-200",
    description: "Can be handled when convenient"
  };
};

const getSentimentDisplay = (sentiment?: string | null) => {
  if (!sentiment) return null;
  
  const s = sentiment.toLowerCase();
  if (s === "positive") {
    return { 
      label: "Positive Tone", 
      emoji: "😊", 
      color: "bg-green-50 text-green-700 border-green-200",
      description: "Friendly and upbeat communication"
    };
  }
  if (s === "negative") {
    return { 
      label: "Concerned Tone", 
      emoji: "😕", 
      color: "bg-red-50 text-red-700 border-red-200",
      description: "May require careful handling"
    };
  }
  if (s === "neutral") {
    return { 
      label: "Professional Tone", 
      emoji: "😐", 
      color: "bg-slate-50 text-slate-700 border-slate-200",
      description: "Standard business communication"
    };
  }
  return { 
    label: sentiment, 
    emoji: "📝", 
    color: "bg-gray-50 text-gray-700 border-gray-200",
    description: "Tone detected"
  };
};

const getDirectionDisplay = (direction?: string) => {
  if (direction === 'incoming') {
    return {
      label: "Received",
      color: "bg-blue-50 text-blue-700 border-blue-200",
      icon: "📩"
    };
  }
  if (direction === 'outgoing') {
    return {
      label: "Sent",
      color: "bg-slate-50 text-slate-700 border-slate-200", 
      icon: "📤"
    };
  }
  return null;
};

export const ImprovedEmailCard = ({
  email,
  onArchive,
  onFlag,
  isArchiving,
  isFlagging,
}: ImprovedEmailCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const priorityInfo = getPriorityDisplay(email.priorityScore);
  const sentimentInfo = getSentimentDisplay(email.sentiment);
  const directionInfo = getDirectionDisplay(email.email_direction);

  return (
    <Card className={`transition-all duration-200 hover:shadow-md ${
      email.is_flagged 
        ? 'bg-amber-50 border-amber-200 shadow-sm' 
        : 'bg-white hover:bg-slate-50'
    }`}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-slate-25 transition-colors pb-3">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  {directionInfo && (
                    <Badge variant="outline" className={`text-xs ${directionInfo.color}`}>
                      {directionInfo.icon} {directionInfo.label}
                    </Badge>
                  )}
                  {email.is_flagged && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                      🚩 Flagged
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-xs text-slate-600">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatDistanceToNow(email.receivedAt, { addSuffix: true })}
                  </Badge>
                </div>
                
                <h3 className="font-medium text-slate-900 leading-tight line-clamp-2 mb-1">
                  {email.subject}
                </h3>
                
                <div className="flex items-center text-sm text-slate-600 mb-2">
                  <User className="h-3.5 w-3.5 mr-1.5" />
                  {email.sender}
                </div>
                
                <p className="text-sm text-slate-700 line-clamp-2 leading-relaxed">
                  {email.summary}
                </p>
              </div>
              
              <div className="flex flex-col items-end gap-2">
                <div className="flex flex-wrap justify-end gap-1">
                  {priorityInfo && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${priorityInfo.color}`}
                      title={priorityInfo.description}
                    >
                      {priorityInfo.emoji} {priorityInfo.label}
                    </Badge>
                  )}
                  {sentimentInfo && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${sentimentInfo.color}`}
                      title={sentimentInfo.description}
                    >
                      {sentimentInfo.emoji} {sentimentInfo.label}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-slate-500 hover:text-slate-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(!isExpanded);
                    }}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Hide Details
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        View Details
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            <Separator className="mb-4" />
            
            {/* Assignment Section */}
            {email.assignedTo ? (
              <div className="flex items-center mb-4 p-3 bg-blue-50 rounded-lg">
                <Building className="h-4 w-4 mr-2 text-blue-600" />
                <span className="text-sm text-blue-800 mr-2">Assigned to:</span>
                <Badge variant={email.assignedTo.type === 'candidate' ? 'secondary' : 'outline'}>
                  {email.assignedTo.name}
                </Badge>
              </div>
            ) : (
              email.processing_status === 'ai_processing_complete' && (
                <div className="flex items-center mb-4 p-3 bg-amber-50 rounded-lg">
                  <Target className="h-4 w-4 mr-2 text-amber-600" />
                  <span className="text-sm text-amber-800">Requires assignment to proceed</span>
                </div>
              )
            )}

            {/* Categories Section */}
            {email.categories && email.categories.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center text-sm font-medium text-slate-700 mb-2">
                  <LayoutGrid className="h-4 w-4 mr-2" />
                  Categories
                </div>
                <div className="flex flex-wrap gap-2">
                  {email.categories.map((category) => (
                    <Badge key={category} variant="secondary" className="text-xs">
                      {category.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Tags Section */}
            {email.tags && email.tags.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center text-sm font-medium text-slate-700 mb-2">
                  <Tag className="h-4 w-4 mr-2" />
                  Tags
                </div>
                <div className="flex flex-wrap gap-2">
                  {email.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Key Entities Section */}
            {email.extractedEntities && Object.keys(email.extractedEntities).length > 0 && (
              <div className="mb-4">
                <div className="flex items-center text-sm font-medium text-slate-700 mb-2">
                  <Info className="h-4 w-4 mr-2" />
                  Key Information
                </div>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {Object.entries(email.extractedEntities).map(([key, value]) => (
                    <div key={key} className="flex items-baseline text-sm">
                      <span className="font-medium text-slate-700 mr-2 min-w-0 flex-shrink-0">
                        {key.replace(/_/g, ' ')}:
                      </span>
                      <span className="text-slate-600">
                        {Array.isArray(value) ? value.join(', ') : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Next Steps Section */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Recommended Next Steps</h4>
              <div className="bg-blue-50 rounded-lg p-3">
                <ul className="space-y-2">
                  {email.nextSteps.map((step, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <span className="mr-2 text-blue-600 font-medium">{index + 1}.</span>
                      <span className="text-slate-700">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Actions Section */}
            <div className="flex justify-between items-center pt-3 border-t border-slate-200">
              <a 
                href={email.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 flex items-center hover:text-blue-800 transition-colors"
              >
                <LinkIcon className="h-4 w-4 mr-1" />
                View Original Email
              </a>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(email.id);
                  }}
                  disabled={isArchiving || isFlagging}
                  className="h-8"
                >
                  {isArchiving ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Archive className="h-4 w-4 mr-1" />
                  )}
                  Archive
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFlag(email.id, email.is_flagged);
                  }}
                  disabled={isFlagging || isArchiving}
                  className="h-8"
                >
                  {isFlagging ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Flag className={`h-4 w-4 mr-1 ${email.is_flagged ? 'text-amber-600' : ''}`} />
                  )}
                  {email.is_flagged ? 'Unflag' : 'Flag'}
                </Button>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
